import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { subscribe } from "@/lib/subscribe.functions";

type Frequency = "daily" | "weekly" | "monthly";

const OPTIONS = [
  { value: "daily" as const, label: "Dagligt" },
  { value: "weekly" as const, label: "Ugentligt" },
  { value: "monthly" as const, label: "Månedligt" },
];

export function SubscribeSection(_props: { subscriberCount?: number | null } = {}) {
  const subscribeFn = useServerFn(subscribe);
  const [email, setEmail] = useState("");
  const [frequency, setFrequency] = useState<Frequency>("daily");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [message, setMessage] = useState<string>("");


  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await subscribeFn({ data: { email, frequency } });
      setStatus("ok");
      setMessage(
        res.already
          ? "Din adresse er allerede noteret. Tjek din indbakke for bekræftelses-mailen."
          : "Tak. Vi har sendt dig en mail – bekræft venligst tilmeldingen ved at klikke på linket.",
      );
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Noget gik galt. Prøv igen om lidt.");
    }
  }

  return (
    <section id="tilmeld">
      <p className="eyebrow mb-3" style={{ color: "var(--accent, #7a3b2e)" }}>Brevet</p>

      <h2 className="font-serif text-[26px] md:text-[32px] leading-[1.08] mb-3 text-ink">
        Et stille brev, når der er noget værd at læse
      </h2>
      <p className="font-serif font-light text-[15px] md:text-[16px] leading-[1.65] text-ink-soft mb-6">
        Kun de dage, hvor der er fundet nyt. Ingen reklamer, ingen sporing. Du bekræfter din
        tilmelding via et link i mailen.
      </p>

      <form onSubmit={onSubmit}>
        <p className="font-serif italic font-light text-[14px] text-muted mb-2">Hvor ofte</p>
        <div role="radiogroup" aria-label="Hvor ofte" className="flex w-full mb-3">
          {OPTIONS.map((opt, i) => {
            const active = frequency === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex-1 text-center font-serif text-[15px] py-3 px-1 border border-ink cursor-pointer transition-colors ${
                  i > 0 ? "border-l-0" : ""
                } ${active ? "bg-ink text-paper" : "bg-transparent text-ink-soft hover:text-ink"}`}
              >
                <input
                  type="radio"
                  name="frequency"
                  value={opt.value}
                  checked={active}
                  onChange={() => setFrequency(opt.value)}
                  className="sr-only"
                />
                {opt.label}
              </label>
            );
          })}
        </div>

        <div className="flex">
          <label className="sr-only" htmlFor="subscribe-email">Din e-mail</label>
          <input
            id="subscribe-email"
            type="email"
            required
            maxLength={255}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Din e-mail"
            disabled={status === "loading"}
            className="min-w-0 flex-1 font-serif text-[16px] px-4 py-3 border border-ink bg-transparent text-ink outline-none placeholder:italic placeholder:text-muted focus:border-accent focus:shadow-[0_0_0_3px_rgba(122,59,46,0.2)] transition"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="font-serif text-[16px] px-5 py-3 border border-ink border-l-0 bg-transparent text-ink cursor-pointer whitespace-nowrap hover:bg-[rgba(122,59,46,0.10)] hover:text-accent hover:border-accent transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:opacity-50"
          >
            {status === "loading" ? "Sender…" : "Skriv mig op"}
          </button>
        </div>
        <p className="mt-3 font-serif italic font-light text-[13px] text-muted">
          Du bekræfter din tilmelding via et link i mailen – og kan altid skifte frekvens eller afmelde nederst i hvert brev.
        </p>
      </form>

      {status === "ok" && (
        <p className="mt-4 font-serif italic text-[14px] text-accent">{message}</p>
      )}
      {status === "error" && (
        <p className="mt-4 font-serif italic text-[14px] text-accent">{message}</p>
      )}

    </section>

  );
}
